import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "../../../components/select";
import { useDispatch, useSelector } from "react-redux";
import arrow from "../../../assets/svg-icons/blueArrow.svg";
import close from "../../../assets/svg-icons/closeBrand.svg";
import { Link, useNavigate } from "react-router-dom";
import { useVolumeSnapshotsList } from "../layout/resuableEffect";
import Modal from "../../../components/modal";
import { deleteVolumeSnapshot, updateVolumeSnapshot } from "../../../hooks/local/userReducer";
import Input from "../../../components/input";
import TextArea from "../../../components/textArea";
import Button from "../../../components/button";
import usePartnerNavigation from "../../../hooks/usePartnerNavigation";


const ManageVolumeSnapshots = () => {

  document.title = "Manage Volume Snapshots - CloudVantage";

  const dispatch = useDispatch();
  const { navigate, generatePartnerUrl } = usePartnerNavigation();


  const projectId = useSelector((state) => state.user.selectedProject)?.id;

  const options = [
    { value: "all-snapshots", label: "All snapshots" },
    { value: "name", label: "Name" },
    { value: "status", label: "Status" },
  ];

  const [manageSnapshotModal, setManageSnapshotModal] = useState(false);
  const [isDeleteImageModal, setIsDeleteImageModal] = useState(false);
  const [editSnapshotModal, setEditSnapshotModal] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;

  const volumeSnapshotList = useVolumeSnapshotsList();

  const volumeSnapshotListMap = Array.isArray(volumeSnapshotList)
    ? volumeSnapshotList.map((volume, index) => ({
        sn: index + 1,
        id: volume?.id,
        name: volume?.name || "N/A",
        volumeName: volume?.volume?.name || "N/A",
        description: volume?.description || "N/A",
        status: volume?.status || "N/A",
        size: volume?.size || "N/A",
        groupSnapshot: volume?.groupSnapshot || "N/A",
        date: volume?.created_on,
      }))
    : [];

  const showManageSnapshotModal = (key, data) => {
    setManageSnapshotModal(key);
    const snapshotDetails = { snapshotId: data.id, snapshotName: data.name, snapshotSize: data.size };
    sessionStorage.setItem("selectedVolumeSnapshot", JSON.stringify(snapshotDetails));
  };

  const closeManageSnapshotModal = () => {
    setManageSnapshotModal(false);
  };

  const toggleEditSnapshotModal = (snapshot) => {
    setSelectedSnapshot(snapshot);
    setEditSnapshotModal(!!snapshot); 
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const filteredData = volumeSnapshotListMap?.filter(
    (row) =>
      row?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      row?.status?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleServerAction = async (action, payload) => {
    const { payload: response } = await dispatch(action(payload));
    if (response.statuscode === "00") {
      window.location.reload();
    }
  };

  const deleteSnapshot = (snapshotId) =>
    handleServerAction(deleteVolumeSnapshot, { projectId, snapshotId });

  const editSnapshot = useFormik({
    initialValues: {
      snapshotName: selectedSnapshot?.name || "",
      description: selectedSnapshot?.description || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      snapshotName: Yup.string().required("Snapshot name is required"),
    }),
    onSubmit: async (values) => {
      const { snapshotName, description } = values;
      const snapshotData = {
        snapshotId: selectedSnapshot.id,
        name: snapshotName,
        description,
        projectId,
      };

      await handleServerAction(updateVolumeSnapshot, snapshotData);
      toggleEditSnapshotModal(null);
    },
  });

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue">
        Manage Volume Snapshots
      </p>

      {volumeSnapshotListMap?.length > 0 ? (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
            <div className="md:col-span-3">
              <Select label="Filter snapshots" name="snapshots" options={options} />
            </div>
            <div className="md:col-span-4">
              <input
                type="search"
                value={searchText}
                onChange={handleSearchChange}
                placeholder="Search by mode of payment, status..."
                className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>
          <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
            <table className="w-full text-sm text-left overflow-hidden">
              <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                <tr>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">S/N</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Name</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Description</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Size</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Status</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Volume Name</th>
                  <th scope="col" className="px-6 py-4 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] border-t-2 text-sm">
                {currentEntries.map((row, index) => (
                  <tr
                    key={index}
                    className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                  >
                    <td className="text-start py-4 px-6">{row?.sn}.</td>
                    <td className="text-start py-4 px-6">{row?.name}.</td>
                    <td className="text-start py-4 px-6">{row?.description}</td>
                    <td className="text-start py-4 px-6">{row?.size}</td>
                    <td className="text-start py-4 px-6">{row?.status}</td>
                    <td className="text-start py-4 px-6">{row?.volumeName}</td>
                    <td className="text-start py-4 px-6">
                      <button
                        className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                        onClick={() => showManageSnapshotModal(index, row)}
                      >
                        Manage Snapshot
                      </button>

                      {manageSnapshotModal === index && (
                        <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                          <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                            <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                              <div className="flex gap-2 items-center">
                                <span className="dark:text-white">Manage "{row.name}"</span>
                              </div>
                              <img
                                src={close}
                                onClick={closeManageSnapshotModal}
                                className="h-5 cursor-pointer"
                                alt=""
                              />
                            </div>
                            <div className="p-4 grid gap-1">
                              <Link
                                to={generatePartnerUrl("/createVolume")}
                                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all"
                              >
                                <span>Create volume</span>
                                <span>
                                  <img src={arrow} alt="" className="" />
                                </span>
                              </Link>
                              <div
                                className="flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all cursor-pointer"
                                onClick={() => {
                                  closeManageSnapshotModal();
                                  toggleEditSnapshotModal(row);
                                }}
                              >
                                <span>Edit volume snapshot</span>
                                <span>
                                  <img src={arrow} alt="" className="" />
                                </span>
                              </div>
                              <div
                                className="flex justify-self-center text-red-500 rounded-md py-5 px-3 text-center cursor-pointer"
                                onClick={() => {
                                  setIsDeleteImageModal(true);
                                }}
                              >
                                <span className="hover:underline">Delete volume snapshot</span>
                              </div>
                            </div>
                            <Modal
                              title={"Delete Snapshot"}
                              isVisible={isDeleteImageModal}
                              onClose={() => setIsDeleteImageModal(false)}
                            >
                              <div className="text-sm text-center pt-6 text-wrap">
                                Are you sure you want to delete{" "}
                                <span className="underline">{row.name}</span>. This action cannot be
                                undone.
                              </div>
                              <div className="grid gap-4 justify-center py-6">
                                <button
                                  className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm w-fit"
                                  onClick={() => deleteSnapshot(row.id)}
                                >
                                  Yes, delete {row.name}
                                </button>
                                <button
                                  className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
                                  onClick={() => setIsDeleteImageModal(false)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </Modal>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4">
            <p className="text-gray-400 text-sm">
              Showing{" "}
              <span className="font-medium text-black dark:text-white">
                {indexOfFirstEntry + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-black dark:text-white">
                {Math.min(indexOfLastEntry, volumeSnapshotListMap?.length)}
              </span>{" "}
              of{" "}
              <span className="font-medium text-black dark:text-white">
                {volumeSnapshotListMap?.length}
              </span>{" "}
              results
            </p>
            <div className="flex gap-3">
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                  currentPage === 1 ? "opacity-50" : "opacity-100"
                }`}
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                  currentPage === totalPages ? "opacity-50" : "opacity-100"
                }`}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="md:px-52 text-center grid text-gray-500 mt-32">
          <div>
            It looks like you don't have a volume snapshot yet. Once you make a snapshot for a volume,
            your volume snapshot details will appear here.
          </div>
        </div>
      )}

      {/* Edit Snapshot Modal */}
      {editSnapshotModal && selectedSnapshot && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Edit Snapshot "{selectedSnapshot?.name}"</p>
              <span onClick={() => toggleEditSnapshotModal(null)} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>

            <form className="gap-y-6 grid" onSubmit={editSnapshot.handleSubmit}>
              <Input
                label={"Snapshot Name"}
                type={"text"}
                name={"snapshotName"}
                value={editSnapshot.values.snapshotName}
                onBlur={editSnapshot.handleBlur}
                onChange={editSnapshot.handleChange}
                onError={
                  editSnapshot.errors.snapshotName && editSnapshot.touched.snapshotName
                    ? editSnapshot.errors.snapshotName
                    : null
                }
              />
              <TextArea
                label={"Description"}
                type={"text"}
                name={"description"}
                value={editSnapshot.values.description}
                onBlur={editSnapshot.handleBlur}
                onChange={editSnapshot.handleChange}
                onError={
                  editSnapshot.errors.description && editSnapshot.touched.description
                    ? editSnapshot.errors.description
                    : null
                }
              />
              <div className="flex justify-between items-center gap-2">
                <Button buttonText={"Update Snapshot"} buttonRole={"submit"} />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageVolumeSnapshots;

